/**
 * Real-Time Security Validation Utilities
 *
 * Provides real-time validation during typing with security warnings.
 * Shows warnings without blocking input for better UX.
 *
 * Main Functions:
 * ├── validateInputRealtime() ← Core validation with security checks
 * └── getWarningColor() ← UI helper for warning display
 */

import {
  createBlockedCharacterRegex,
  FIELD_SECURITY_CONFIGS,
} from '../constants/fieldValidation.constants';
import type {
  SecurityWarning,
  ValidationResult,
} from '../models/validation.model';

// Re-export types for external use
export type {
  SecurityWarning,
  ValidationResult,
} from '../models/validation.model';

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Real-time input validation with comprehensive security checks
 *
 * Validates user input as they type and provides immediate feedback through
 * visual warnings. Performs security validation (dangerous chars, malicious patterns)
 * and basic sanitization checks (spacing, formatting).
 */
export const validateInputRealtime = (
  value: string,
  fieldType:
    | 'text'
    | 'email'
    | 'username'
    | 'name'
    | 'description'
    | 'tournament_password' = 'text',
  fieldName: string = 'input',
): ValidationResult => {
  const originalValue = value;

  // Don't validate empty fields
  if (!value || value.trim() === '') {
    return {
      isValid: true,
      warnings: [],
      sanitizedValue: '',
      originalValue: value,
    };
  }

  const warnings: SecurityWarning[] = [];

  // Get security config for this field type
  const configKey =
    fieldType.toUpperCase() as keyof typeof FIELD_SECURITY_CONFIGS;
  const securityConfig =
    FIELD_SECURITY_CONFIGS[configKey] || FIELD_SECURITY_CONFIGS.TEXT;

  // Security validation - check for dangerous characters
  const blockedRegex = createBlockedCharacterRegex(
    securityConfig.blockedCharacters,
  );
  const dangerousChars = value.match(blockedRegex);

  if (dangerousChars) {
    const allowedCharsMap = {
      username: 'a-z, 0-9, _, -',
      name: 'letters, spaces, hyphens',
      email: 'valid email format',
      description: 'letters, numbers, basic punctuation',
      tournament_password: 'letters, numbers, basic punctuation',
      text: 'letters, numbers, basic punctuation',
    };

    warnings.push({
      type: 'security',
      severity: 'high',
      message: `Invalid characters. Use: ${allowedCharsMap[fieldType] || allowedCharsMap.text}`,
    });
  }

  // Check for malicious patterns
  const hasMaliciousPatterns = securityConfig.patterns.some((pattern) =>
    pattern.test(value),
  );

  if (hasMaliciousPatterns) {
    warnings.push({
      type: 'security',
      severity: 'high',
      message: `${fieldName} contains invalid characters`,
    });
  }

  // Check max length
  if (value.length > securityConfig.maxLength) {
    warnings.push({
      type: 'security',
      severity: 'high',
      message: `Max ${securityConfig.maxLength} characters`,
    });
  }

  // Sanitization validation - basic cleaning
  const cleanedValue = originalValue.replace(/\s+/g, ' ').trim();

  if (cleanedValue !== originalValue && cleanedValue.length > 0) {
    let sanitizationReason = '';

    if (originalValue.includes('  ') && !cleanedValue.includes('  ')) {
      sanitizationReason = 'extra spaces';
    } else if (
      originalValue !== originalValue.trim() &&
      cleanedValue === originalValue.trim()
    ) {
      sanitizationReason = 'leading/trailing spaces';
    }

    if (sanitizationReason) {
      warnings.push({
        type: 'content',
        severity: 'low',
        message: `Will be auto-cleaned: ${sanitizationReason}`,
      });
    }
  }

  return {
    isValid: warnings.filter((w) => w.severity === 'high').length === 0,
    warnings,
    sanitizedValue: cleanedValue,
    originalValue,
  };
};

/**
 * Helper function to get warning color for UI display
 *
 * @param severity - Warning severity level
 * @returns CSS color string for the severity level
 */
export const getWarningColor = (severity: 'low' | 'high'): string => {
  switch (severity) {
    case 'high':
      return '#f44336'; // Red
    case 'low':
      return '#2196f3'; // Blue
    default:
      return '#757575'; // Grey
  }
};
